import React, { useState } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Grid,
    Card as MUICard,
    CardMedia,
    Stack,
    IconButton,
    Divider,
    Link as MuiLink,
    useMediaQuery,
    Button,
    Paper,
    Drawer,
    Tooltip,
    Container,
    AppBar,
    Toolbar,
    Snackbar,
    Alert,
    SvgIcon
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
    Phone,
    Email,
    Language,
    LinkedIn,
    GitHub,
    Instagram,
    Business,
    Download,
    WhatsApp,
    Facebook,
    ContactPhone,
    EmojiEvents,
    PictureAsPdf,
    VideoLibrary,
    YouTube,
    PlayArrow
} from '@mui/icons-material';
import './DefaultTheme.css';
import { saveContactToDevice } from '../../../utils/vCardUtils.js';
import { trackCardInteraction } from '../../../api/analyticsApi';
import { getFullUrl } from '../../../utils/urlUtils';

// Custom X (former Twitter) Icon
const XIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </SvgIcon>
);

const socialIcons = {
    linkedin: <LinkedIn sx={{ color: '#0077b5' }} />,
    github: <GitHub sx={{ color: '#333' }} />,
    twitter: <Box sx={{ 
        backgroundColor: '#000', 
        borderRadius: '8px', 
        width: '24px', 
        height: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: '#000',
        }
    }}>
        <XIcon sx={{ color: '#fff', fontSize: '16px' }} />
    </Box>,
    x: <Box sx={{ 
        backgroundColor: '#000', 
        borderRadius: '8px', 
        width: '24px', 
        height: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: '#000',
        }
    }}>
        <XIcon sx={{ color: '#fff', fontSize: '16px' }} />
    </Box>,
    instagram: <Instagram sx={{ color: '#e4405f' }} />,
    facebook: <Facebook sx={{ color: '#1877f2' }} />,
    tiktok: <Language sx={{ color: '#000' }} />,
    website: <Language sx={{ color: '#2575fc' }} />,
    whatsapp: <WhatsApp sx={{ color: '#25d366' }} />,
};

const getContactIcon = (type) => {
    switch (type) {
        case 'email': return <Email sx={{ fontSize: 18, color: '#fff' }} />;
        case 'phone': return <Phone sx={{ fontSize: 18, color: '#fff' }} />;
        case 'whatsapp': return <WhatsApp sx={{ fontSize: 18, color: '#fff' }} />;
        default: return <Business sx={{ fontSize: 18, color: '#fff' }} />;
    }
};

const getContactLink = (contact, cardHolderName) => {
    const { type, value, country } = contact;
    
    if (type === 'email') {
        return `mailto:${value}`;
    } else if (type === 'phone') {
        const prefix = country === 'SG' ? '+65' : '+91';
        return `tel:${prefix}${value}`;
    } else if (type === 'whatsapp') {
        const prefix = country === 'SG' ? '65' : '91';
        const defaultMessage = encodeURIComponent(`Hello ${cardHolderName || 'there'}, How are you? Shall we talk about your services?`);
        return `https://wa.me/${prefix}${value}?text=${defaultMessage}`;
    }
    return '#';
};

const getContactDisplay = (contact) => {
    const { type, value, country } = contact;
    
    if (type === 'email') {
        return value;
    } else if (type === 'phone' || type === 'whatsapp') {
        const prefix = country === 'SG' ? '+65' : '+91';
        return `${prefix} ${value}`;
    }
    return value;
};

const getContactBackground = (type) => {
    switch (type) {
        case 'email': return 'linear-gradient(120deg, #1976d2 0%, #1565c0 100%)';
        case 'phone': return 'linear-gradient(120deg, #4caf50 0%, #388e3c 100%)';
        case 'whatsapp': return 'linear-gradient(120deg, #25d366 0%, #128c7e 100%)';
        default: return 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)';
    }
};

// Helper function to get image URL - handles both File objects and URLs
const getImageUrl = (value) => {
    if (!value) return null;
    if (value instanceof File) return URL.createObjectURL(value);
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('blob'))) return value;
    if (typeof value === 'string') return getFullUrl(value);
    return null;
};

const HeaderBar = styled(AppBar)(({ theme }) => ({
    background: 'rgba(255,255,255,0.95)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    color: '#333',
    [theme.breakpoints.down('md')]: {
        background: '#fff',
    },
}));

const HeroSection = styled(Box)(({ theme }) => ({
    minHeight: '85vh',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(8, 0),
    background: '#f8fafc',
    [theme.breakpoints.down('md')]: {
        minHeight: 'auto',
        padding: theme.spacing(4, 0),
    },
}));

const ServicesSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8, 0),
    background: '#fff',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(4, 0),
    },
}));

const ServiceCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    },
}));

const GlassCard = styled(Paper)(({ theme }) => ({
    background: 'rgba(255,255,255,0.85)',
    borderRadius: 24,
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1.5px solid rgba(200,200,200,0.12)',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        borderRadius: 12,
    },
}));

const MasonryGrid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    justifyContent: 'center',
}));

const CoverSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '50vh',
    minHeight: 300,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    alignItems: 'flex-end',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, transparent 0%, rgba(248, 250, 252, 0.3) 50%, rgba(248, 250, 252, 0.8) 100%)',
        zIndex: 1,
    },
    '& > *': {
        position: 'relative',
        zIndex: 2,
    },
    [theme.breakpoints.down('md')]: {
        height: '35vh',
        minHeight: 250,
    },
}));

const VideoContainer = {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%', /* 16:9 Aspect Ratio */
    marginBottom: '16px',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#000',
    cursor: 'pointer',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    minHeight: '350px' /* Increased minimum height */
};

const VideoOverlay = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.5)',
    }
};

const YouTubeEmbed = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '16px'
};

const DefaultTheme = ({ card, isPreview, isEditMode }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')) || isPreview;
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm')) || isPreview;
    
    // Snackbar state for notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const showNotification = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const [playingVideos, setPlayingVideos] = useState({});

    const toggleVideo = (achievementId) => {
        setPlayingVideos(prev => ({
            ...prev,
            [achievementId]: !prev[achievementId]
        }));
    };

    // Helper function to extract YouTube video ID and create embed URL
    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : null;
    };

    const getYouTubeEmbedUrl = (url) => {
        const videoId = getYouTubeVideoId(url);
        if (!videoId) return null;
        return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0&showinfo=1`;
    };

    // Data extraction
    const name = card.name || 'Your Name';
    const title = card.designation || 'Your Designation';
    const companyName = card.company_name || '';
    const about = card.about || card.description || '';
    const profileImg = getImageUrl(card.profile_picture_url) || '';
    const companyLogo = getImageUrl(card.company_logo_url) || '';
    const coverPhoto = getImageUrl(card.cover_photo_url) || '';
    const gallery = Array.isArray(card.product_images) ? card.product_images : [];
    const socialLinks = Array.isArray(card.social_profiles) ? card.social_profiles : [];
    const contacts = Array.isArray(card.contacts) ? card.contacts : [];
    const website = card.website || '';

    // Scale for preview mode - Miniature effect
    const scale = isPreview ? 0.4 : 1;

    // Animations
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] } }
    };
    const fadeInLeft = {
        hidden: { opacity: 0, x: -40 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] } }
    };
    const fadeInRight = {
        hidden: { opacity: 0, x: 40 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.2, ease: [0.6, -0.05, 0.01, 0.99] } }
    };
    const galleryItemAnim = {
        hidden: { opacity: 0, scale: 0.95, y: 30 },
        visible: (i = 1) => ({
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { duration: 0.6, delay: i * 0.1, ease: [0.43, 0.13, 0.23, 0.96] }
        })
    };

    return (
        <Box sx={{ 
            minHeight: isPreview ? '250vh' : '100vh', 
            background: '#f8fafc', 
            fontFamily: 'Poppins, Inter, sans-serif',
            transform: `scale(${scale})`, 
            transformOrigin: 'top left', 
            width: isPreview ? '250%' : '100%',
            overflowX: 'hidden',
            overflowY: isPreview ? 'visible' : 'auto'
        }}>
            {/* Cover Photo Section */}
            {coverPhoto && (
                <CoverSection sx={{ backgroundImage: `url(${coverPhoto})` }}>
                    <Container maxWidth="lg" sx={{ pb: 4 }}>
                        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                {companyLogo && (
                                    <Avatar 
                                        src={companyLogo} 
                                        alt="Company Logo" 
                                        sx={{ 
                                            width: { xs: 120, md: 150 }, 
                                            height: { xs: 120, md: 150 },
                                            border: '4px solid rgba(255, 255, 255, 0.9)',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                            background: '#fff'
                                        }} 
                                    />
                                )}
                            </Box>
                        </motion.div>
                    </Container>
                </CoverSection>
            )}
            
            {/* Header/Navigation */}
            <HeaderBar position="sticky" elevation={0}>
                <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
                            {title}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {contacts.slice(0, 2).map((contact) => (
                            <Typography key={contact.id} variant="body2" sx={{ color: '#666', display: { xs: 'none', md: 'block' } }}>
                                {contact.value}
                            </Typography>
                        ))}
                        <Stack direction="row" spacing={1}>
                                                    {socialLinks.slice(0, 4).map((profile) => (
                            <IconButton
                                component="a"
                                href={profile.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                                    // Track social media interaction
                trackCardInteraction(card.slug, 'social_click', profile.platform.toLowerCase());
                                }}
                                sx={{ 
                                    '&:hover': { 
                                        transform: 'scale(1.1)',
                                        transition: 'transform 0.2s'
                                    } 
                                }}
                                key={profile.id}
                                size="small"
                            >
                                {socialIcons[profile.platform.toLowerCase()] || <Language sx={{ color: '#2575fc' }} />}
                            </IconButton>
                        ))}
                        </Stack>
                    </Stack>
                </Toolbar>
            </HeaderBar>

            {/* Hero Section */}
            <HeroSection>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <motion.div variants={fadeInLeft} initial="hidden" animate="visible">
                                <Typography variant={isMobile ? 'h3' : 'h1'} sx={{ fontWeight: 700, color: '#1a202c', mb: 2 }}>
                                    Hello, I'm {name.split(' ')[0]}!
                                </Typography>
                                <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ color: '#4a5568', mb: 3, fontWeight: 400 }}>
                                    {title}{companyName && ` in ${companyName}`} —
                                </Typography>
                                {about && (
                                    <Typography variant="body1" sx={{ color: '#718096', mb: 4, fontSize: 18, lineHeight: 1.6 }}>
                                        {about}
                                    </Typography>
                                )}
                                
                                {/* Contact Information List - Desktop Only */}
                                {contacts.length > 0 && (
                                    <Box sx={{ mt: 4, display: { xs: 'none', md: 'block' } }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', mb: 3 }}>
                                            Get In Touch
                                        </Typography>
                                        <Stack spacing={2}>
                                            {contacts.slice(0, 4).map((contact, index) => (
                                                <Box
                                                    key={contact.id || index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 2,
                                                        borderRadius: 2,
                                                        background: 'rgba(255, 255, 255, 0.8)',
                                                        border: '1px solid rgba(37, 117, 252, 0.1)',
                                                        transition: 'all 0.3s ease',
                                                        cursor: ['email', 'phone', 'whatsapp'].includes(contact.type) ? 'pointer' : 'default',
                                                        '&:hover': {
                                                            background: 'rgba(37, 117, 252, 0.05)',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 8px 25px rgba(37, 117, 252, 0.15)',
                                                            borderColor: 'rgba(37, 117, 252, 0.3)',
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        const link = getContactLink(contact, card.name);
                                                        if (link !== '#') {
                                                            // Track contact interaction
                                                            trackCardInteraction(card.slug, 'contact_click', contact.type);
                                                            window.open(link, contact.type === 'whatsapp' ? '_blank' : '_self');
                                                        }
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '50%',
                                                            background: getContactBackground(contact.type),
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mr: 2,
                                                            flexShrink: 0,
                                                            boxShadow: '0 4px 12px rgba(37, 117, 252, 0.3)',
                                                        }}
                                                    >
                                                        {getContactIcon(contact.type)}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                color: '#718096', 
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: 0.5,
                                                                display: 'block',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {contact.type}
                                                        </Typography>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                color: '#1a202c', 
                                                                fontSize: '0.9rem',
                                                                fontWeight: 600,
                                                                wordBreak: 'break-all',
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {getContactDisplay(contact)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </motion.div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <motion.div variants={fadeInRight} initial="hidden" animate="visible">
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                    <Avatar
                                        src={profileImg}
                                        alt={name}
                                        sx={{
                                            width: isMobile ? 280 : 400,
                                            height: isMobile ? 280 : 400,
                                            border: '8px solid #fff',
                                            boxShadow: '0 16px 64px rgba(0,0,0,0.15)',
                                            background: '#e2e8f0',
                                            mb: { xs: 3, md: 0 }
                                        }}
                                    />
                                    
                                    {/* Contact Information List - Mobile Only (Below Profile Picture) */}
                                    {contacts.length > 0 && (
                                        <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%', maxWidth: 320 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', mb: 2, textAlign: 'center' }}>
                                                Get In Touch
                                            </Typography>
                                            <Stack spacing={2}>
                                                {contacts.slice(0, 4).map((contact, index) => (
                                                    <Box
                                                        key={contact.id || index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: 2,
                                                            borderRadius: 2,
                                                            background: 'rgba(255, 255, 255, 0.8)',
                                                            border: '1px solid rgba(37, 117, 252, 0.1)',
                                                            transition: 'all 0.3s ease',
                                                            cursor: ['email', 'phone', 'whatsapp'].includes(contact.type) ? 'pointer' : 'default',
                                                            '&:hover': {
                                                                background: 'rgba(37, 117, 252, 0.05)',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 8px 25px rgba(37, 117, 252, 0.15)',
                                                                borderColor: 'rgba(37, 117, 252, 0.3)',
                                                            },
                                                        }}
                                                        onClick={() => {
                                                            const link = getContactLink(contact, card.name);
                                                            if (link !== '#') {
                                                                // Track contact interaction
                                                                trackCardInteraction(card.slug, 'contact_click', contact.type);
                                                                window.open(link, contact.type === 'whatsapp' ? '_blank' : '_self');
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 35,
                                                                height: 35,
                                                                borderRadius: '50%',
                                                                background: getContactBackground(contact.type),
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mr: 2,
                                                                flexShrink: 0,
                                                                boxShadow: '0 4px 12px rgba(37, 117, 252, 0.3)',
                                                            }}
                                                        >
                                                            {getContactIcon(contact.type)}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography 
                                                                variant="caption" 
                                                                sx={{ 
                                                                    color: '#718096', 
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: 0.5,
                                                                    display: 'block',
                                                                    mb: 0.25,
                                                                }}
                                                            >
                                                                {contact.type}
                                                            </Typography>
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: '#1a202c', 
                                                                    fontSize: '0.85rem',
                                                                    fontWeight: 600,
                                                                    wordBreak: 'break-all',
                                                                    lineHeight: 1.1,
                                                                }}
                                                            >
                                                                {getContactDisplay(contact)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </HeroSection>

            {/* Batches Section */}
            {card.batches && card.batches.length > 0 && (
                <Box sx={{ py: 8, background: '#fff' }}>
                    <Container maxWidth="lg">
                        <GlassCard as={motion.section} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 4, color: '#1a202c', textAlign: 'center' }}>
                                🎓 Batches
                            </Typography>
                            <MasonryGrid container spacing={3}>
                                {card.batches.map((batch, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={batch.id || index}>
                                        <motion.div
                                            variants={galleryItemAnim}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true, amount: 0.3 }}
                                            custom={index}
                                            whileHover={{ scale: 1.04, boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)' }}
                                            style={{ borderRadius: 16, overflow: 'hidden', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                                        >
                                            <Box sx={{ position: 'relative' }}>
                                                {batch.image_url && (
                                                    <CardMedia
                                                        component="img"
                                                        image={getImageUrl(batch.image_url)}
                                                        alt={batch.caption || `Batch ${index + 1}`}
                                                        sx={{
                                                            height: isMobile ? 180 : 240,
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
                                                        }}
                                                    />
                                                )}
                                                <Box sx={{ p: 2 }}>
                                                    {batch.caption && (
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c', mb: 1 }}>
                                                            {batch.caption}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </MasonryGrid>
                        </GlassCard>
                    </Container>
                </Box>
            )}

            {/* Services/Content Section */}
            {card.contents && card.contents.length > 0 && (
                <ServicesSection>
                    <Container maxWidth="lg">
                        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, textAlign: 'center', mb: 6, color: '#1a202c' }}>
                                Top Services
                            </Typography>
                            <Grid container spacing={4}>
                                {card.contents.map((content, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                        <ServiceCard elevation={0}>
                                            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                                <Box sx={{ width: 64, height: 64, borderRadius: 2, background: 'linear-gradient(120deg, #e0eafc 0%, #cfdef3 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Business sx={{ fontSize: 32, color: '#2575fc' }} />
                                                </Box>
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', mb: 1 }}>
                                                {content.field_name}
                                            </Typography>
                                            <Box 
                                                className="quill-content"
                                                dangerouslySetInnerHTML={{ __html: content.field_value }}
                                                sx={{
                                                    color: '#718096',
                                                    lineHeight: 1.6,
                                                    '& p': { margin: 0 },
                                                    '& a': { color: 'primary.main' }
                                                }}
                                            />
                                        </ServiceCard>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    </Container>
                </ServicesSection>
            )}

            {/* Gallery Section */}
            {gallery.length > 0 && (
                <Box sx={{ py: 8, background: '#f8fafc' }}>
                    <Container maxWidth="lg">
                        <GlassCard as={motion.section} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 4, color: '#1a202c', textAlign: 'center' }}>
                                Featured Projects
                            </Typography>
                            <MasonryGrid container spacing={3}>
                                {gallery.map((image, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={image.id || index}>
                                        <motion.div
                                            variants={galleryItemAnim}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true, amount: 0.3 }}
                                            custom={index}
                                            whileHover={{ scale: 1.04, boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)' }}
                                            style={{ borderRadius: 16, overflow: 'hidden', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                                        >
                                            <Box sx={{ position: 'relative' }}>
                                                <CardMedia
                                                    component="img"
                                                    image={getImageUrl(image.url)}
                                                    alt={image.caption || `Gallery image ${index + 1}`}
                                                    sx={{
                                                        height: isMobile ? 180 : 240,
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
                                                    }}
                                                />
                                                {image.caption && (
                                                    <Box sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c' }}>
                                                            {image.caption}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </MasonryGrid>
                        </GlassCard>
                    </Container>
                </Box>
            )}

            {/* Achievements Section */}
            {card.achievements && card.achievements.length > 0 && (
                <Box sx={{ py: 8, background: '#fff' }}>
                    <Container maxWidth="lg">
                        <GlassCard as={motion.section} variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 4, color: '#1a202c', textAlign: 'center' }}>
                                🏆 Achievements & Awards
                            </Typography>
                            <MasonryGrid container spacing={4}>
                                {card.achievements.map((achievement, index) => (
                                    <Grid item xs={12} sm={6} md={6} lg={4} key={achievement.id || index}>
                                        <motion.div
                                            variants={galleryItemAnim}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true, amount: 0.3 }}
                                            custom={index}
                                            whileHover={{ scale: 1.04, boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)' }}
                                            style={{ borderRadius: 16, overflow: 'hidden', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', minHeight: '400px' }}
                                        >
                                            <Box sx={{ position: 'relative' }}>
                                                {achievement.image_url && !achievement.youtube_url && (
                                                    <CardMedia
                                                        component="img"
                                                        image={getImageUrl(achievement.image_url)}
                                                        alt={achievement.caption || `Achievement ${index + 1}`}
                                                        sx={{
                                                            height: isMobile ? 220 : 280,
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
                                                        }}
                                                        onError={(e) => {
                                                            console.error('Image load error:', e, 'URL:', getImageUrl(achievement.image_url));
                                                            e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                                                        }}
                                                    />
                                                )}
                                                {achievement.youtube_url && (
                                                    <Box sx={VideoContainer}>
                                                        {!playingVideos[achievement.id || index] ? (
                                                            <Box 
                                                                onClick={() => toggleVideo(achievement.id || index)}
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/maxresdefault.jpg`}
                                                                    alt={achievement.caption || `Achievement ${index + 1}`}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                    onError={(e) => {
                                                                        // Fallback to hqdefault if maxresdefault fails
                                                                        e.target.src = `https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/hqdefault.jpg`;
                                                                        e.target.onerror = () => {
                                                                            e.target.src = 'https://via.placeholder.com/400x300?text=Video+Thumbnail+Not+Found';
                                                                        };
                                                                    }}
                                                                />
                                                                <Box sx={{
                                                                    ...VideoOverlay,
                                                                    '&:hover': {
                                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                                        '& .play-icon': {
                                                                            transform: 'scale(1.1)',
                                                                            opacity: 1
                                                                        }
                                                                    }
                                                                }}>
                                                                    <PlayArrow 
                                                                        className="play-icon"
                                                                        sx={{
                                                                            fontSize: '64px',
                                                                            color: 'white',
                                                                            opacity: 0.9,
                                                                            transition: 'all 0.3s ease'
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                component="iframe"
                                                                src={getYouTubeEmbedUrl(achievement.youtube_url)}
                                                                title={achievement.caption || `Achievement ${index + 1}`}
                                                                sx={YouTubeEmbed}
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                allowFullScreen
                                                                referrerPolicy="strict-origin-when-cross-origin"
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                <Box sx={{ p: 3 }}>
                                                    {achievement.caption && (
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c', mb: 2 }}>
                                                            {achievement.caption}
                                                        </Typography>
                                                    )}
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                                        {achievement.certificate_link && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                href={achievement.certificate_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                startIcon={<EmojiEvents />}
                                                                sx={{
                                                                    borderColor: '#2575fc',
                                                                    color: '#2575fc',
                                                                    textTransform: 'none',
                                                                    fontSize: '0.75rem',
                                                                    '&:hover': {
                                                                        background: '#2575fc',
                                                                        color: '#fff',
                                                                    }
                                                                }}
                                                            >
                                                                Certificate
                                                            </Button>
                                                        )}
                                                        {achievement.pdf_url && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                href={getImageUrl(achievement.pdf_url)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                startIcon={<PictureAsPdf />}
                                                                sx={{
                                                                    borderColor: '#dc2626',
                                                                    color: '#dc2626',
                                                                    textTransform: 'none',
                                                                    fontSize: '0.75rem',
                                                                    '&:hover': {
                                                                        background: '#dc2626',
                                                                        color: '#fff',
                                                                    }
                                                                }}
                                                            >
                                                                PDF
                                                            </Button>
                                                        )}
                                                        {achievement.video_url && (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                href={getImageUrl(achievement.video_url)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                startIcon={<VideoLibrary />}
                                                                sx={{
                                                                    borderColor: '#0891b2',
                                                                    color: '#0891b2',
                                                                    textTransform: 'none',
                                                                    fontSize: '0.75rem',
                                                                    '&:hover': {
                                                                        background: '#0891b2',
                                                                        color: '#fff',
                                                                    }
                                                                }}
                                                            >
                                                                Video
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            </Box>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </MasonryGrid>
                        </GlassCard>
                    </Container>
                </Box>
            )}

            {/* Save to Contacts Button */}
            {!isPreview && !isEditMode && (
                <Box sx={{ py: 4, textAlign: 'center', background: '#fff' }}>
                    <Container maxWidth="lg">
                        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                            <Button 
                                variant="outlined" 
                                startIcon={<ContactPhone />}
                                onClick={() => {
                                    // Track vCard download
                                    trackCardInteraction(card.slug, 'vcard_download', 'save_contact');
                                    const success = saveContactToDevice(card);
                                    if (success) {
                                        showNotification('Contact saved successfully! 📱', 'success');
                                    } else {
                                        showNotification('Failed to save contact. Please try again.', 'error');
                                    }
                                }}
                                sx={{ 
                                    borderRadius: 8, 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderColor: '#2575fc',
                                    color: '#2575fc',
                                    background: 'rgba(37, 117, 252, 0.05)',
                                    border: '2px solid #2575fc',
                                    '&:hover': {
                                        background: '#2575fc',
                                        color: '#fff',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(37, 117, 252, 0.25)',
                                    }
                                }}
                            >
                                Save to Contacts
                            </Button>
                        </motion.div>
                    </Container>
                </Box>
            )}

            {/* Website Button */}
            {website && (
                <Box sx={{ py: 4, textAlign: 'center', background: '#f8fafc' }}>
                    <Container maxWidth="lg">
                        <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                href={website} 
                                target="_blank" 
                                sx={{ 
                                    borderRadius: 8, 
                                    boxShadow: 2, 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5
                                }}
                            >
                                Visit Website
                            </Button>
                        </motion.div>
                    </Container>
                </Box>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DefaultTheme; 